import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, AbstractControl, Validators, ValidatorFn } from '@angular/forms';
import { AdminService } from '../../services/admin.service';
import { TimeSheet } from '../../models/TimeSheet';
import { GetSitesService } from '../../services/sites.service';
import { TokenService } from 'src/app/services/getData_token.service';

@Component({
  selector: 'app-time-entry',
  templateUrl: './time-entry.component.html',
  styleUrls: ['./time-entry.component.css']
})
export class TimeEntryComponent implements OnInit {

  // Use local device time with proper local date formatting
  range = new FormGroup({
    start: new FormControl(this.getLocalDateString()),
    end: new FormControl(this.getLocalDateString())
  });

  // Helper method to get local date in YYYY-MM-DD format without timezone conversion
  getLocalDateString(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  get startControl() {
    return this.range.controls.start as FormControl;
  }

  get endControl() {
    return this.range.controls.end as FormControl;
  }
  
  selected = 'time-entry';
  totalHours = 0;
  todaysHours = 0;
  dataSource: TimeSheet[] = [];
  filteredDataSource: TimeSheet[] = [];
  userName!:any;
  projects: string[] = [];
  tasks: any[] = [];
  successMessage: string | null = null;
  inlineEditErrorMessage: string | null = null; // New property for inline edit errors
  isLoading = false;
  submitted = false;
  isSubmitting = false;
  private originalData: { [key: number]: TimeSheet } = {};
  addEntryError: string | null = null; 

  constructor(private adminService: AdminService, private sitesService: GetSitesService, private tokenService: TokenService) { }

  ngOnInit(): void {
    this.loadTimeSheets();
    this.loadProjects();
    this.loadTasks();
    const userDetails = this.tokenService.getUserDetails();
    if (userDetails) {
     this.userName = userDetails.name;
    }

    this.range.valueChanges.subscribe(value => {
      if (!value.start && this.range.get('end')?.value) {
        this.range.get('end')!.reset(null, { emitEvent: false });
      }
      this.filterData();
    });
  }

  loadTimeSheets() {
    this.isLoading = true;
    this.adminService.getTimeSheets().subscribe(response => {
      this.dataSource = response.data.records;
      this.filterData();
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
    });
  }

  loadProjects(): void {
    this.sitesService.getTimeSheetProjetcs().subscribe(
      (projects: string[]) => {
        this.projects = projects;
      },
      (error) => {
        console.error('Error fetching sites:');
      }
    );
  }

  loadTasks(): void {
    this.adminService.getAllTasks().subscribe(
      (response: any) => {
        this.tasks = response.data;
      },
      (error) => {
        console.error('Error fetching tasks:');
      }
    );
  }

  filterData() {
    const { start, end } = this.range.value;
    if (start && end) {
      const startDate = new Date(start + 'T00:00:00');
      const endDate = new Date(end + 'T23:59:59');

      this.filteredDataSource = this.dataSource.filter(e => {
        const itemDate = new Date(e.entryDate);
        return itemDate >= startDate && itemDate <= endDate;
      });
    } else {
      this.filteredDataSource = this.dataSource;
    }
    this.filteredDataSource.sort((a, b) => {
      const dateA = new Date(a.entryDate.split('T')[0]);
      const dateB = new Date(b.entryDate.split('T')[0]);

      // Primary sort: entryDate descending (newest first)
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime();
      }

      // Secondary sort: startDateTime descending (latest time first)
      const fullTimeA = new Date(a.entryDate.split('T')[0] + 'T' + (a.startDateTime.includes('T') ? a.startDateTime.split('T')[1] : a.startDateTime));
      const fullTimeB = new Date(b.entryDate.split('T')[0] + 'T' + (b.startDateTime.includes('T') ? b.startDateTime.split('T')[1] : b.startDateTime));
      
      return fullTimeB.getTime() - fullTimeA.getTime();
    });
    this.calculateHours();
  }

  // In calculateHours method
  calculateHours() {
    this.totalHours = this.filteredDataSource.reduce((acc, curr) => acc + curr.hours, 0);
    const today = this.getLocalDateString();
    this.todaysHours = this.filteredDataSource.filter(e => e.entryDate.slice(0, 10) === today).reduce((acc, curr) => acc + curr.hours, 0);
  }

  formatHours(decimalHours: number): string {
    if (isNaN(decimalHours) || decimalHours < 0) {
      return '0h 0m';
    }
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  }

  deleteEntry(element: TimeSheet) {
    this.adminService.deleteProjectTimeSheet(element.id).subscribe(() => {
      this.dataSource = this.dataSource.filter(e => e.id !== element.id);
      this.filterData();
    });
  }

  // New properties for adding a new entry
  newEntryForm = new FormGroup({
    project: new FormControl(''),
    task: new FormControl(''),
    date: new FormControl(this.getLocalDateString(), Validators.required),
    startTime: new FormControl('', Validators.required),
    endTime: new FormControl('', Validators.required),
    description: new FormControl('')
  }, { validators: [this.timeRangeValidator(), this.projectOrTaskValidator()] });

  // Custom validator for project/task
  projectOrTaskValidator(): ValidatorFn {
    return (form: AbstractControl): { [key: string]: boolean } | null => {
      const project = form.get('project')?.value;
      const task = form.get('task')?.value;

      if (!project && !task) {
        return { 'projectOrTaskRequired': true };
      }

      return null;
    };
  }

  // Custom validator to ensure end time is greater than start time
  timeRangeValidator(): ValidatorFn {
    return (form: AbstractControl): { [key: string]: boolean } | null => {
      const startTime = form.get('startTime')?.value;
      const endTime = form.get('endTime')?.value;

      if (startTime && endTime) {
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);

        const startDate = new Date();
        startDate.setHours(startHour, startMinute, 0, 0);

        const endDate = new Date();
        endDate.setHours(endHour, endMinute, 0, 0);

        if (endDate <= startDate) {
          return { 'timeInvalid': true };
        }
      }
      return null;
    };
  }

  // In addNewEntry method
  addNewEntry() {
    this.submitted = true;
    if (this.newEntryForm.invalid || this.isSubmitting) {
      if (this.isSubmitting) return;
      this.addEntryError = 'Please ensure required fields are complete and end time is after start time.';
      setTimeout(() => {
        this.addEntryError = null;
      }, 3000);
      return;
    }

    // Reset the date to current local date if needed
    if (!this.newEntryForm.get('date')?.value) {
      this.newEntryForm.get('date')?.setValue(this.getLocalDateString());
    }
    
    this.isSubmitting = true;
    const formValues = this.newEntryForm.value;
    const date = new Date(formValues.date);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const entryDate = `${year}-${month}-${day}`;
    const payload = {
      entryDate: `${entryDate}T00:00:00`,
      startDateTime: `${entryDate}T${formValues.startTime}:00`,
      endDateTime: `${entryDate}T${formValues.endTime}:00`,
      project: formValues.project,
      task: formValues.task,
      description: formValues.description
    };

    this.adminService.addProjectTimeSheet(payload).subscribe(
      (response) => {
        this.successMessage = 'Time entry added successfully!';
        this.loadTimeSheets();
        this.newEntryForm.reset({date: this.getLocalDateString()});
        this.submitted = false;
        this.isSubmitting = false;
        setTimeout(() => this.successMessage = null, 3000);
      },
      (error) => {
        console.error('Error adding time entry:');
        this.successMessage = 'Error adding time entry. Please try again.';
        this.isSubmitting = false;
        setTimeout(() => this.successMessage = null, 3000);
      }
    );
  }

  // New methods for inline editing
  editInlineEntry(element: TimeSheet) {
    this.originalData[element.id] = { ...element };
    element.editable = true;
  
    // Use local date formatting instead of UTC
    const entryDate = new Date(element.entryDate);
    const year = entryDate.getFullYear();
    const month = (entryDate.getMonth() + 1).toString().padStart(2, '0');
    const day = entryDate.getDate().toString().padStart(2, '0');
    element.entryDate = `${year}-${month}-${day}`;

    const startTime = new Date(element.startDateTime);
    const startHours = startTime.getHours().toString().padStart(2, '0');
    const startMinutes = startTime.getMinutes().toString().padStart(2, '0');
    element.startDateTime = `${startHours}:${startMinutes}`;

    const endTime = new Date(element.endDateTime);
    const endHours = endTime.getHours().toString().padStart(2, '0');
    const endMinutes = endTime.getMinutes().toString().padStart(2, '0');
    element.endDateTime = `${endHours}:${endMinutes}`;
  }

  saveInlineEntry(element: TimeSheet) {
    if (!element.project && !element.task) {
      this.inlineEditErrorMessage = 'Please select either a Project or a Task.';
      setTimeout(() => this.inlineEditErrorMessage = null, 3000);
      return;
    }
    // Validate inline edited times
    const [startHour, startMinute] = element.startDateTime.split(':').map(Number);
    const [endHour, endMinute] = element.endDateTime.split(':').map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0, 0);

    if (endDate <= startDate) {
      this.inlineEditErrorMessage = 'End Time must be greater than Start Time for inline edit.';
      setTimeout(() => this.inlineEditErrorMessage = null, 3000);
      return; // Prevent saving if validation fails
    }

    const date = new Date(element.entryDate);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const entryDate = `${year}-${month}-${day}`;

    const payload = {
      entryDate: `${entryDate}T00:00:00`,
      startDateTime: `${entryDate}T${element.startDateTime}:00`,
      endDateTime: `${entryDate}T${element.endDateTime}:00`,
      project: element.project,
      task: element.task,
      description: element.description
    };

    this.adminService.updateProjectTimeSheet(element.id, payload).subscribe(
      (response) => {
        this.successMessage = 'Time entry updated successfully!';
        this.loadTimeSheets();
        element.editable = false;
        setTimeout(() => this.successMessage = null, 3000);
      },
      (error) => {
        console.error('Error updating time entry:');
        this.inlineEditErrorMessage = 'Error updating time entry. Please try again.';
        setTimeout(() => this.inlineEditErrorMessage = null, 3000);
      }
    );
  }
   cancelInlineEntry(element: TimeSheet) {
    const original = this.originalData[element.id];
    if (original) {
      Object.assign(element, original);
      delete this.originalData[element.id];
    }
    element.editable = false;
  }
  // Set now method for time controls - fixed parameter type
  setNow(controlName: string) {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    this.newEntryForm.get(controlName)?.setValue(`${hours}:${minutes}`);
  }
}
